import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Users, MessageCircle } from "lucide-react";
import { listCircles } from "@/lib/community.functions";
import { absoluteUrl } from "@/lib/site";
import { FollowBuddhaButton } from "@/components/community/FollowBuddhaButton";

export const Route = createFileRoute("/circles/")({
  component: CirclesPage,
  head: () => ({
    meta: [
      { title: "Discussion Circles · KAfrequency" },
      { name: "description", content: "Discussion circles for KAfrequency practitioners — reflections, questions, and sacred talk with the community." },
      { property: "og:title", content: "Discussion Circles · KAfrequency" },
      { property: "og:description", content: "Join the KAfrequency community. Discuss teachings, share reflections, and practice together." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/circles") },
      { property: "og:site_name", content: "Buddha KA · KAfrequency" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/circles") }],
  }),
});

function CirclesPage() {
  const listFn = useServerFn(listCircles);
  const q = useQuery({ queryKey: ["circles"], queryFn: () => listFn(), staleTime: 60_000 });

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20">
      <header className="mb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">The Community</p>
        <h1 className="mt-4 font-serif text-5xl sm:text-6xl">Discussion Circles</h1>
        <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
          Practitioners gather here to reflect, ask, and speak. Every circle is a small temple.
        </p>
        <div className="mt-8">
          <FollowBuddhaButton />
        </div>
      </header>

      {q.isLoading && <p className="text-sm text-muted-foreground">Loading circles…</p>}
      {q.data?.length === 0 && <p className="text-sm text-muted-foreground">No circles yet.</p>}

      <ul className="grid gap-6 sm:grid-cols-2">
        {q.data?.map((c) => (
          <li key={c.id}>
            <Link
              to="/circles/$slug"
              params={{ slug: c.slug }}
              className="group flex h-full flex-col justify-between rounded-2xl border hairline bg-obsidian/40 p-6 transition-all hover:-translate-y-0.5 hover:border-gold hover:shadow-gold min-h-[220px]"
            >
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
                  <Users size={12} /> Circle
                </div>
                <h2 className="mt-3 font-serif text-2xl text-ivory group-hover:text-gold-luminous">{c.title}</h2>
                {c.description && (
                  <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{c.description}</p>
                )}
              </div>
              <div className="mt-6 flex items-center gap-2 text-xs text-gold">
                <MessageCircle size={12} /> Enter the circle →
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
